<template>
  <section class="section">
    <!-- CONFIRM REMOVAL MODAL -->
    <div class="modal" :class="{ 'is-active': productToRemove }" v-if="productToRemove">
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('products.texts.confirmRemovalTitle') }}</p>
          <button class="delete" aria-label="close" data-bulma-modal="close" @click="removeCancel()"></button>
        </header>
        <section class="modal-card-body">
          <p v-html="$t('products.texts.confirmRemoval', { productName: productToRemove.name })"></p>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-danger" @click="removeConfirm()">Confirm</button>
          <button class="button" data-bulma-modal="close" @click="removeCancel()">Cancel</button>
        </footer>
      </div>
    </div>
    <!-- / CONFIRM REMOVAL MODAL -->

    <!-- BULK UPLOAD MODALS -->
    <div class="modal" :class="{ 'is-active': showInvalidFileModal }">
      <div class="modal-background" @click="showInvalidFileModal = false"></div>
      <div class="modal-content">
        <div class="box">
          <p>{{ $t('products.texts.invalidFileMessage') }}</p>
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="showInvalidFileModal = false"></button>
    </div>

    <div class="modal" :class="{ 'is-active': showUploadSuccessModal }">
      <div class="modal-background" @click="showUploadSuccessModal = false"></div>
      <div class="modal-content">
        <div class="box">
          <p>{{ $t('products.texts.uploadSuccessMessage') }}</p>
          <p>{{ $t('products.texts.uploadSuccessAdditionalMessage') }}</p>
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="showUploadSuccessModal = false"></button>
    </div>

    <div class="modal" :class="{ 'is-active': showUploadErrorModal }">
      <div class="modal-background" @click="showUploadErrorModal = false"></div>
      <div class="modal-content">
        <div class="box">
          <p>{{ $t('products.texts.uploadErrorMessage') }}</p>
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="showUploadErrorModal = false"></button>
    </div>
    <div class="modal" :class="{ 'is-active': showUploadTimeoutModal }">
      <div class="modal-background" @click="showUploadTimeoutModal = false"></div>
      <div class="modal-content">
        <div class="box">
          <p>{{ $t('products.texts.uploadTimeoutMessage') }}</p>
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="showUploadTimeoutModal = false"></button>
    </div>
    <!-- / BULK UPLOAD MODALS -->

    <!-- MAIN CONTENT -->
    <div class="columns is-centered">
      <div class="column is-full-width">
        <h3 class="title is-4 level">
          <div class="level-left">
            <div class="level-item">
              <span>{{ $t('products.title') }}</span>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <router-link :to="{ name: 'AddProduct' }" class="nav-link">
                <add-button :label="$t('app.buttons.add')" class="is-fullwidth"></add-button>
              </router-link>
              <button
                class="button is-info is-fullwidth ml-3 is-hidden-mobile"
                :class="{ 'is-loading': isBulkChecking }"
                :disabled="isBulkChecking"
                @click="triggerFileInput()"
              >
                {{ $t('products.buttons.bulkUpload') }}
              </button>
              <input ref="fileInput" type="file" accept=".csv,.xlsx,.xls" style="display: none" @change="handleFileSelect" />
              <publish-button :label="$t('app.buttons.publishAll')" @click="publishAll()"
                :disabled="batchSelection.length === 0" class="is-fullwidth ml-3 is-hidden-mobile"
                :loading="isPublishing"></publish-button>
              <unpublish-button :label="$t('app.buttons.unpublishAll')" @click="unpublishAll()"
                :disabled="batchSelection.length === 0" class="is-fullwidth ml-3 is-hidden-mobile"
                :loading="isUnpublishing"></unpublish-button>
            </div>
          </div>
        </h3>

        <div class="field">
          <label class="label">{{ $t('products.fields.name') }}</label>
          <p class="control has-icons-left has-icons-right">
            <input class="input" type="text" v-model="filterName" :placeholder="$t('app.texts.search')" />
            <span class="icon is-small is-left">
              <i class="fa fa-search"></i>
            </span>
          </p>
        </div>
        <div class="field">
          <label class="label">{{ $t('products.fields.status') }}</label>
          <div class="select is-fullwidth">
            <select v-model="filterStatus">
              <option v-for="item in products.statuses" :key="item.value" :value="item.value">{{ item.name }}</option>
            </select>
          </div>
        </div>
        <div class="field">
          <search-button :label="$t('app.buttons.search')" @click="search()" class="is-fullwidth"></search-button>
        </div>
        <div class="table-container">
          <table class="table is-fullwidth is-hoverable is-striped">
            <thead>
              <tr>
                <th class="is-hidden-mobile" style="width: 5%">
                  <input class="checkbox" type="checkbox" v-model="batchAllSelection" />
                </th>
                <th class="is-hidden-mobile" style="width: 5%">{{ $t('products.fields.id') }}</th>
                <th style="width: 20%">{{ $t('products.fields.name') }}</th>
                <th style="width: 15%">{{ $t('products.fields.status') }}</th>
                <th class="is-hidden-mobile" style="width: 15%">{{ $t('products.fields.price') }}</th>
                <th style="width: 10%">{{ $t('products.fields.netWeight') }}</th>
                <th style="width: 10%">{{ $t('products.fields.grossWeight') }}</th>
                <th style="width: 20%"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in paginatedProducts" :key="product.id">
                <td class="is-hidden-mobile"><input class="checkbox" type="checkbox" :value="product.id"
                    v-model="batchSelection" /></td>
                <td class="is-hidden-mobile">{{ product.id }}</td>
                <td>
                  {{ product.name }}
                </td>
                <td><status-tag
                    :status="product.isPublished ? $t('products.statuses.active') : $t('products.statuses.draft')"></status-tag>
                </td>
                <td class="is-hidden-mobile"> {{ product.priceCurrency }} {{ product.price }} </td>
                <td class="is-hidden-mobile"> {{ product.netWeight }} {{ product.netWeightUnit }} </td>
                <td class="is-hidden-mobile"> {{ product.grossWeight }} {{ product.grossWeightUnit }} </td>
                <td>
                  <router-link :to="{
                    name: 'EditProduct',
                    params: {
                      id: product.id,
                    },
                  }" class="nav-link">
                    <edit-button :label="$t('app.buttons.edit')" class="is-fullwidth"></edit-button>
                  </router-link>
                  <publish-button v-if="!product.isPublished" :label="$t('app.buttons.publish')" class="is-fullwidth"
                    @click="publish(product)"></publish-button>
                  <unpublish-button v-if="product.isPublished" :label="$t('app.buttons.unpublish')" class="is-fullwidth"
                    @click="publish(product)"></unpublish-button>
                  <delete-button :label="$t('app.buttons.delete')" class="is-fullwidth"
                    @click="removeDialog(product)"></delete-button>
                </td>
              </tr>
            </tbody>
          </table>
          <Pagination :currentPage="currentPage" :totalItems="products.items.length" :pageSize="pageSize"
            @page-change="onPageChange" />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import StatusTag from '../../components/forms/StatusTag.vue'
import Dropdown from '../../components/forms/Dropdown.vue'
import AddButton from '../../components/forms/AddButton.vue'
import DeleteButton from '../../components/forms/DeleteButton.vue'
import PublishButton from '../../components/forms/PublishButton.vue'
import UnpublishButton from '../../components/forms/UnpublishButton.vue'
import EditButton from '../../components/forms/EditButton.vue'
import SearchButton from '../../components/forms/SearchButton.vue'
import Pagination from '../../components/Pagination.vue'

import { FooshiApi } from '@/services/fooshiApi.service'

export default {
  components: { StatusTag, Dropdown, AddButton, PublishButton, UnpublishButton, DeleteButton, EditButton, SearchButton, Pagination },
  name: 'Products',
  watch: {
    batchAllSelection (newVal, oldVal) {
      this.batchSelection.length = 0
      if (newVal) {
        this.products.items.forEach(p => this.batchSelection.push(p.id))
      }
    }
  },
  methods: {
    search () {
      var filter = {}
      if (this.filterName && this.filterName.trim()) {
        filter.name = this.filterName.trim()
      }

      if (this.filterStatus !== undefined) {
        filter.status = this.filterStatus
      }

      return this.fetchProducts(filter)
    },
    async fetchProducts (filter = {}) {
      var response = await FooshiApi.getAllProducts(filter)
      if (response.status) {
        // clear current list of items
        this.products.items.length = 0

        response.data.forEach(product => {
          product.status = this.$t('products.statuses.notAvailable')
          product.categories = product.categories.map(category => category.id)
          product.validationMessage = ''
          this.products.items.push(product)
        })
        this.products.items.sort((a, b) => {
          return a.name.localeCompare(b.name)
        })
      }
    },
    async removeDialog (product) {
      this.productToRemove = product
    },
    async removeCancel () {
      this.productToRemove = undefined
    },
    async removeConfirm () {
      const response = await FooshiApi.removeProduct(this.productToRemove.id)
      if (response.success) {
        await this.fetchProducts()
      } else {
        // todo: toast
      }
      this.productToRemove = undefined
    },
    async publish (product) {
      const productToUpdate = {
        ...product,
        isPublished: !product.isPublished
      }
      const response = await FooshiApi.updateProduct(productToUpdate)
      if (response.success) {
        await this.fetchProducts()
      } else {
        product.validationMessage = response.message[0]
      }
    },
    async unpublishAll () {
      this.isUnpublishing = true
      const response = await FooshiApi.updateAllProducts({
        payload: { isPublished: false },
        items: this.batchSelection
      })
      if (response.success) {
        await this.fetchProducts()
      } else {
        // todo: toast
      }
      this.isUnpublishing = false
    },
    async publishAll () {
      this.isPublishing = true
      const response = await FooshiApi.updateAllProducts({
        payload: { isPublished: true },
        items: this.batchSelection
      })
      if (response.success) {
        await this.fetchProducts()
      } else {
        // todo: toast
      }
      this.isPublishing = false
    },
    onPageChange (newPage) {
      this.currentPage = newPage
    },
    triggerFileInput () {
      this.$refs.fileInput.click()
    },
    validateFile (file) {
      const maxSize = 10 * 1024 * 1024 // 10MB in bytes
      const allowedTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ]
      const allowedExtensions = ['.csv', '.xls', '.xlsx']

      const fileExtension = '.' + file.name.split('.').pop().toLowerCase()

      if (file.size > maxSize) {
        return false
      }

      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        return false
      }

      return true
    },
    async handleFileSelect (event) {
      const file = event.target.files[0]
      if (!file) {
        return
      }

      // Reset file input
      event.target.value = ''

      if (!this.validateFile(file)) {
        this.showInvalidFileModal = true
        return
      }

      try {
        const response = await FooshiApi.uploadDocument(file)
        if (response.success) {
          this.showUploadSuccessModal = true
          const batchUploadId = response.data && (response.data.batchUploadId || response.data.id)
          if (batchUploadId) {
            this.startStatusPolling(batchUploadId)
          }
        } else {
          this.showUploadErrorModal = true
        }
      } catch (error) {
        this.showUploadErrorModal = true
      }
    },
    startStatusPolling (batchUploadId) {
      this.clearStatusPolling()
      this.isBulkChecking = true
      this.statusPollAttempts = 0
      this.pollUploadStatus(batchUploadId)
    },
    async pollUploadStatus (batchUploadId) {
      this.statusPollAttempts += 1
      try {
        const statusResponse = await FooshiApi.getUploadDocumentStatus(batchUploadId)
        const processed = statusResponse.success && statusResponse.data && statusResponse.data.processed
        if (processed) {
          this.clearStatusPolling()
          this.isBulkChecking = false
          await this.fetchProducts()
          return
        }
      } catch (error) {
        this.clearStatusPolling()
        this.isBulkChecking = false
        this.showUploadErrorModal = true
        return
      }

      if (this.statusPollAttempts < this.maxStatusAttempts) {
        this.statusPollTimeoutId = setTimeout(() => this.pollUploadStatus(batchUploadId), 5000)
      } else {
        this.clearStatusPolling()
        this.isBulkChecking = false
        this.showUploadTimeoutModal = true
      }
    },
    clearStatusPolling () {
      if (this.statusPollTimeoutId) {
        clearTimeout(this.statusPollTimeoutId)
        this.statusPollTimeoutId = null
      }
    }
  },
  async mounted () {
    await this.fetchProducts()
    this.ready = true
  },
  beforeDestroy () {
    this.clearStatusPolling()
  },
  computed: {
    productList: function () {
      return this.products.items
    },
    paginatedProducts () {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.productList.slice(start, end)
    }
  },
  data () {
    return {
      batchAllSelection: false,
      isPublishing: false,
      isUnpublishing: false,
      batchSelection: [],
      filterName: undefined,
      filterStatus: undefined,
      products: {
        statuses: [
          {},
          { name: this.$t('products.statuses.active'), value: 1 },
          { name: this.$t('products.statuses.draft'), value: 0 }
        ],
        items: []
      },
      productToRemove: undefined,
      currentPage: 1,
      pageSize: 10,
      showInvalidFileModal: false,
      showUploadSuccessModal: false,
      showUploadErrorModal: false,
      showUploadTimeoutModal: false,
      isBulkChecking: false,
      statusPollAttempts: 0,
      statusPollTimeoutId: null,
      maxStatusAttempts: 24
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/products/Products.vue